import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faNetworkWired, faPlus, faSave, faTrash, faPlug, 
    faServer, faKey, faDatabase, faSpinner, faCheckCircle,
    faExclamationCircle, faToggleOn, faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Spinner, Card } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const ConnectionManager = ({ onRefresh }) => {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [selectedConn, setSelectedConn] = useState(null);

    const emptyConn = {
        ConnName: '',
        ConnDriver: 'mysql',
        ConnHost: '',
        ConnPort: 3306,
        ConnUser: '',
        ConnPass: '',
        ConnDB: '',
        ConnActive: 1
    };

    useEffect(() => {
        fetchConnections();
    }, []);

    const fetchConnections = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/connections', {}, r => {
            if (r.success) setConnections(r.data || []);
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const handleSave = () => {
        if (!selectedConn.ConnName || !selectedConn.ConnHost) {
            Notify({ type: 'fail', text: 'Nombre y Host son requeridos' });
            return;
        }
        setSaving(true);
        const method = selectedConn.ConnId ? 'Put' : 'Post';
        const url = selectedConn.ConnId ? `superadmin/connections/${selectedConn.ConnId}` : 'superadmin/connections';

        request[method](API_BASE + url, selectedConn, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Conexión guardada' });
                fetchConnections();
                setSelectedConn(null);
                if (onRefresh) onRefresh();
            } else {
                Notify({ type: 'fail', text: 'Error al guardar' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const handleTest = () => {
        setTesting(true);
        request.Post(API_BASE + 'superadmin/connections/test', selectedConn, r => {
            if (r.success) {
                Notify({ type: 'success', text: r.msg || 'Conexión exitosa' });
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error de conexión' });
            }
            setTesting(false);
        });
    };

    const handleDelete = (id) => {
        if (!window.confirm('¿Eliminar esta conexión?')) return;
        request.Delete(API_BASE + `superadmin/connections/${id}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Conexión eliminada' });
                fetchConnections();
            }
        });
    };

    return (
        <div className="animate-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="m-0 fw-bold"><FontAwesomeIcon icon={faNetworkWired} className="me-2 text-primary" /> Connection Manager</h5>
                    <p className="text-muted small m-0">Gestiona orígenes de datos externos para orquestación</p>
                </div>
                <button className="sa-btn sa-btn-primary" onClick={() => setSelectedConn(emptyConn)}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nueva Conexión
                </button>
            </div>

            <Row className="g-4">
                {/* List of Connections */}
                <Col lg={4}>
                    <div className="d-flex flex-column gap-3">
                        {loading ? (
                            <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                        ) : connections.length === 0 ? (
                            <Card className="sa-panel p-4 text-center text-slate-400 border-dashed">
                                <FontAwesomeIcon icon={faPlug} className="fs-1 mb-3 opacity-20" />
                                <p className="small">No hay conexiones configuradas</p>
                            </Card>
                        ) : (
                            connections.map(conn => (
                                <motion.div 
                                    key={conn.ConnId}
                                    className={`conn-card ${selectedConn?.ConnId === conn.ConnId ? 'active' : ''}`}
                                    onClick={() => setSelectedConn(conn)}
                                    whileHover={{ x: 5 }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className={`conn-status-dot ${conn.ConnActive ? 'online' : 'offline'}`} />
                                            <div>
                                                <div className="fw-bold fs-6 text-main">{conn.ConnName}</div>
                                                <div className="text-muted small">{conn.ConnDriver.toUpperCase()} • {conn.ConnHost}</div>
                                            </div>
                                        </div>
                                        <button className="btn btn-link text-danger p-0 opacity-75 hover-opacity-100" onClick={(e) => { e.stopPropagation(); handleDelete(conn.ConnId); }}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </div>
                </Col>

                {/* Editor Panel */}
                <Col lg={8}>
                    <AnimatePresence mode="wait">
                        {selectedConn ? (
                            <motion.div 
                                key={selectedConn.ConnId || 'new'}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="sa-panel p-4"
                            >
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h6 className="m-0 fw-bold text-primary">Configuración del Origen</h6>
                                    <div className="d-flex gap-2">
                                        <button className="sa-btn sa-btn-secondary" onClick={handleTest} disabled={testing}>
                                            <FontAwesomeIcon icon={testing ? faSpinner : faPlug} spin={testing} className="me-2" />
                                            Test
                                        </button>
                                        <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                                            <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} className="me-2" />
                                            {selectedConn.ConnId ? 'Actualizar' : 'Guardar'}
                                        </button>
                                    </div>
                                </div>

                                <Row className="g-3">
                                    <Col md={8}>
                                        <label className="sa-label">Alias / Nombre</label>
                                        <input 
                                            type="text" className="sa-input" placeholder="Ej: DB_PRODUCCION_TUC" 
                                            value={selectedConn.ConnName || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnName: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <label className="sa-label">Motor (Driver)</label>
                                        <select 
                                            className="sa-input"
                                            value={selectedConn.ConnDriver || 'mysql'}
                                            onChange={e => setSelectedConn({...selectedConn, ConnDriver: e.target.value})}
                                        >
                                            <option value="mysql">MySQL / MariaDB</option>
                                            <option value="postgres">PostgreSQL</option>
                                            <option value="mssql">SQL Server</option>
                                            <option value="mongodb">MongoDB</option>
                                        </select>
                                    </Col>
                                    
                                    <Col md={9}>
                                        <label className="sa-label"><FontAwesomeIcon icon={faServer} className="me-1" /> Host / IP</label>
                                        <input 
                                            type="text" className="sa-input" placeholder="localhost o 192.168.1.10" 
                                            value={selectedConn.ConnHost || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnHost: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <label className="sa-label">Puerto</label>
                                        <input 
                                            type="number" className="sa-input" placeholder="3306" 
                                            value={selectedConn.ConnPort || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnPort: e.target.value})}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <label className="sa-label">Usuario</label>
                                        <input 
                                            type="text" className="sa-input" 
                                            value={selectedConn.ConnUser || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnUser: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <label className="sa-label"><FontAwesomeIcon icon={faKey} className="me-1" /> Contraseña</label>
                                        <input 
                                            type="password" className="sa-input" 
                                            value={selectedConn.ConnPass || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnPass: e.target.value})}
                                        />
                                    </Col>

                                    <Col md={8}>
                                        <label className="sa-label"><FontAwesomeIcon icon={faDatabase} className="me-1" /> Base de Datos / Schema</label>
                                        <input 
                                            type="text" className="sa-input" 
                                            value={selectedConn.ConnDB || ''}
                                            onChange={e => setSelectedConn({...selectedConn, ConnDB: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={4} className="d-flex align-items-end">
                                        <div className="sa-input d-flex align-items-center justify-content-between">
                                            <span className="small fw-bold">Estado</span>
                                            <FontAwesomeIcon 
                                                icon={selectedConn.ConnActive ? faToggleOn : faToggleOff} 
                                                className={`fs-5 cursor-pointer ${selectedConn.ConnActive ? 'text-primary' : 'text-muted'}`}
                                                onClick={() => setSelectedConn({...selectedConn, ConnActive: !selectedConn.ConnActive})}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <div className="mt-4 p-3 bg-primary bg-opacity-5 rounded-3 border border-primary border-opacity-10">
                                    <div className="d-flex gap-2 text-primary small">
                                        <FontAwesomeIcon icon={faExclamationCircle} className="mt-1" />
                                        <p className="m-0">Asegúrate de que el servidor de DataManager tenga acceso a la IP y puerto especificados (whitelist en el firewall del destino).</p>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted opacity-50">
                                <FontAwesomeIcon icon={faNetworkWired} className="fs-1 mb-3" />
                                <p>Selecciona una conexión para editar o crea una nueva</p>
                            </div>
                        )}
                    </AnimatePresence>
                </Col>
            </Row>

            <style>{`
                .sa-text-contrast { color: var(--text-muted) !important; opacity: 0.9; }
                .conn-card {
                    background: #ffffff;
                    border: 1px solid var(--border-color);
                    padding: 16px;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: var(--shadow-sm);
                }
                .conn-card:hover {
                    background: #f8fafc;
                    border-color: var(--primary);
                }
                .conn-card.active {
                    background: rgba(0, 97, 255, 0.05);
                    border-color: var(--primary);
                    box-shadow: 0 0 20px rgba(0, 97, 255, 0.1);
                }
                .conn-status-dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                }
                .conn-status-dot.online { background: #10b981; box-shadow: 0 0 10px #10b981; }
                .conn-status-dot.offline { background: #94a3b8; }

                .sa-label { color: var(--text-main) !important; font-weight: 600; font-size: 0.8rem; margin-bottom: 6px; display: block; }
                .sa-input::placeholder { color: var(--text-dim); opacity: 0.5; }
            `}</style>
        </div>
    );
};

export default ConnectionManager;
